/**
 * DownloadAppButton Component
 * ===========================
 * Shows a "Windows App" download button in the header.
 * Only visible when accessing from a web browser (not desktop client).
 */

import { useState, useEffect } from 'react'
import { Monitor, Download, X } from 'lucide-react'

interface ClientInfo {
  windows?: {
    version: string
    filename: string
    size_mb: number
    download_url: string
    release_notes?: string
    release_date?: string
  }
}

// Detect if running in Electron desktop app
function isDesktopClient(): boolean {
  // Check for Electron user agent
  if (navigator.userAgent.toLowerCase().includes('electron')) {
    return true
  }
  // Check for exodusDesktop property exposed by preload script
  if (typeof window !== 'undefined') {
    const win = window as { exodusDesktop?: { isDesktopApp?: boolean } }
    if (win.exodusDesktop?.isDesktopApp) {
      return true
    }
  }
  return false
}

export default function DownloadAppButton() {
  const [isDesktop, setIsDesktop] = useState(true) // Default to hidden
  const [showModal, setShowModal] = useState(false)
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check if we're in desktop client on mount
  useEffect(() => {
    setIsDesktop(isDesktopClient())
  }, [])

  // Fetch client info when modal opens
  useEffect(() => {
    if (showModal && !clientInfo) {
      fetchClientInfo()
    }
  }, [showModal, clientInfo])

  const fetchClientInfo = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/client/info')
      if (!response.ok) {
        throw new Error('Failed to fetch client information')
      }
      const data = await response.json()
      setClientInfo(data.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load download information')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = () => {
    if (clientInfo?.windows?.download_url) {
      window.location.href = clientInfo.windows.download_url
    }
  }

  // Don't render if in desktop client
  if (isDesktop) {
    return null
  }

  return (
    <>
      {/* Download Button */}
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
        title="Download Windows App"
      >
        <Monitor size={16} />
        <span className="hidden sm:inline">Windows App</span>
      </button>

      {/* Download Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="relative bg-gradient-to-br from-primary-500 to-primary-700 px-6 py-8 text-white">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/20 transition-colors"
              >
                <X size={20} />
              </button>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Monitor size={32} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Exodus Desktop</h2>
                  <p className="text-primary-100 text-sm">Windows Application</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>
                  <button
                    onClick={fetchClientInfo}
                    className="text-primary-500 hover:text-primary-600 text-sm font-medium"
                  >
                    Try Again
                  </button>
                </div>
              ) : clientInfo?.windows ? (
                <>
                  {/* Version Info */}
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Version</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        v{clientInfo.windows.version}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">File Size</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {clientInfo.windows.size_mb} MB
                      </span>
                    </div>
                    {clientInfo.windows.release_date && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Released</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {new Date(clientInfo.windows.release_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Release Notes */}
                  {clientInfo.windows.release_notes && (
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                        What's New
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {clientInfo.windows.release_notes}
                      </p>
                    </div>
                  )}

                  {/* Download Button */}
                  <button
                    onClick={handleDownload}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold transition-colors"
                  >
                    <Download size={20} />
                    Download for Windows
                  </button>

                  {/* File name */}
                  <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-3">
                    {clientInfo.windows.filename}
                  </p>
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    No Windows client available yet.
                  </p>
                </div>
              )}

              {/* Features */}
              <div className="mt-6 pt-6 border-t dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Desktop Features
                </h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Native Windows experience
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Remembers window size & position
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Microsoft 365 single sign-on
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    Auto-update notifications
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
